'use client';

import { useOnClickOutside } from 'usehooks-ts';
import {
  useRef,
  useState,
  type JSX,
  type RefObject,
  type MouseEventHandler,
  type KeyboardEventHandler,
} from 'react';

import { Badge } from '~/components/badge';
import { HStack } from '~/components/stack';
import { XIcon } from '~/components/icons/XIcon';

interface ITagsStateProps {
  list: string[];
  cached: Record<string, boolean>;
}

interface IInputTagProps {
  value?: string[];
  onChange?: (tags: string[]) => void;
}

export function InputTag({
  onChange,
  value = [],
}: IInputTagProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [tags, setTags] = useState<ITagsStateProps>({
    list: value,
    cached: value.reduce<Record<string, boolean>>((result, v) => {
      result[v] = true;

      return result;
    }, {}),
  });

  const addTag = (tag: string) => {
    if (tags.cached[tag]) {
      return;
    }

    const value = {
      list: [...tags.list, tag],
      cached: {
        ...tags.cached,
        [tag]: true,
      },
    };

    setTags(value);
    onChange?.(value.list);
  };

  const removeTag = (tag: string) => {
    const value = {
      list: tags.list.filter((t) => t !== tag),
      cached: {
        ...tags.cached,
        [tag]: false,
      },
    };

    setTags(value);
    onChange?.(value.list);
  };

  const handleKeydown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    const inputValue = e.currentTarget.value.trim();

    if (
      (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') &&
      inputValue !== ''
    ) {
      e.preventDefault();
      addTag(inputValue);
      e.currentTarget.value = '';
    } else if (e.key === 'Backspace' && inputValue === '') {
      removeTag(tags.list[tags.list.length - 1] || '');
    }
  };

  const handleClickContainer = () => {
    inputRef.current?.focus();
  };

  useOnClickOutside(inputRef as RefObject<HTMLInputElement>, () => {
    if (inputRef.current && inputRef.current.value !== '') {
      addTag(inputRef.current.value);
      inputRef.current.value = '';
    }
  });

  return (
    <HStack
      gap={1}
      onClick={handleClickContainer}
      className='border-1 border-[#ececec] rounded-md px-4 py-[9px] cursor-text flex-wrap min-h-[46px]'
    >
      {tags.list.map((tag) => {
        return <Tag key={tag} value={tag} onRemoveTag={removeTag} />;
      })}
      <input
        ref={inputRef}
        onKeyDown={handleKeydown}
        className='flex-1 border-0 outline-none h-0 focus:h-[unset]'
      />
    </HStack>
  );
}

interface ITagProps {
  value: string;
  onRemoveTag: (tag: string) => void;
}

function Tag({ value, onRemoveTag }: ITagProps): JSX.Element {
  const handleClick: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();

    onRemoveTag(value);
  };

  return (
    <Badge variant='tag' className='items-center h-[26px]'>
      {value}{' '}
      <button type='button' className='cursor-pointer' onClick={handleClick}>
        <XIcon className='size-3.5' />
      </button>
    </Badge>
  );
}
